import styled from "styled-components";
import { tokens } from "../../../../public/tokens";
import { BaseButton } from "../../Button";

const { colors, typography, globals } = tokens;

/**
 * Figma's `shadow-xs` (10% black). Differs from `tokens.effects.effects.shadow.default` (5% #1d1d1d),
 * which renders too faint against the toolbar background.
 */
export const TOOLBAR_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.10)";

/**
 * Base look for every DataTable toolbar button (filter triggers, FilterBar's filter/reset buttons).
 * Deliberately local — the library SelectDropdown/MultiSelectDropdown styles are shared app-wide.
 */
export const toolbarButton = {
    height: "35px",
    padding: `0 ${globals["spacing-2-5"]}`,
    boxShadow: TOOLBAR_SHADOW,
    borderRadius: globals["border-radius"],
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
    lineHeight: "20px",
    whiteSpace: "nowrap",

    "&:focus-visible": {
        outline: `${globals["spacing-px"]} solid ${colors.brand.blue["500"]}`,
        outlineOffset: globals["spacing-0-5"],
    },
};

export const StyledFilterWrapper = styled.div({
    position: "relative",
    display: "inline-flex",
    flexShrink: 0,
});

/**
 * Active (has a selection) follows AMI's programme-table `.btn-selected` look — solid black, white text.
 */
export const StyledFilterTrigger = styled(BaseButton)(({ $active = false, $open = false, $disabled = false }) => {
    const idleBorderColor = $open ? colors.brand.blue["500"] : colors.surfaces["border-divider"];
    return {
        ...toolbarButton,
        background: $active ? colors.neutral.grey["900"] : colors.surfaces["container-background"],
        border: `${globals["spacing-px"]} solid ${$active ? colors.neutral.grey["900"] : idleBorderColor}`,
        color: $active ? colors.neutral.white : colors.text.primary,
        cursor: $disabled ? "not-allowed" : "pointer",
        opacity: $disabled ? 0.6 : 1,
        transition: "border-color 0.15s ease-in-out, background 0.15s ease-in-out",

        "&:hover": $disabled || $active ? {} : { borderColor: colors.brand.blue["500"] },
    };
});

export const StyledFilterChevron = styled.i(({ $open = false }) => ({
    color: "currentColor",
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: "14px",
    transform: $open ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.2s ease-in-out",
}));

/**
 * Menu sizing/spacing follows AMI's `Tablefilter` dropdown (custom.css `.dropdown-comman .dropdown-menu`),
 * with rounded corners.
 */
export const StyledFilterList = styled.ul(({ $top = 0, $left = 0, $width = 0 }) => ({
    position: "fixed",
    top: `${$top}px`,
    left: `${$left}px`,
    width: "max-content",
    minWidth: `max(${$width}px, 210px)`,
    maxWidth: "320px",
    maxHeight: "375px",
    overflowY: "auto",
    margin: 0,
    padding: 0,
    backgroundColor: colors.surfaces["container-background"],
    border: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    borderRadius: globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.hover,
    zIndex: 3000,
    listStyle: "none",
    boxSizing: "border-box",
}));

const filterRow = {
    padding: globals["spacing-2"],
    fontSize: typography.fontSize["md-12px"],
    fontFamily: typography.fontFamilies["albert-sans"],
    lineHeight: "16px",
};

/**
 * `$selected` only bolds the label (single-select has no checkbox); label colour never changes so
 * custom `renderLabel` colours stay intact.
 */
export const StyledFilterItem = styled.li(({ $selected = false, $highlighted = false, $disabled = false }) => ({
    ...filterRow,
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-2-5"],
    cursor: $disabled ? "not-allowed" : "pointer",
    fontWeight: $selected ? typography.fontWeights.semibold : typography.fontWeights.regular,
    color: $disabled ? colors.text.disabled : colors.text.primary,
    background: $highlighted && !$disabled ? colors.neutral.grey["200"] : "transparent",
    opacity: $disabled ? 0.6 : 1,

    "& + li": {
        borderTop: `${globals["spacing-px"]} solid ${colors.neutral.grey["200"]}`,
    },

    "&:hover": $disabled ? {} : { background: colors.neutral.grey["200"] },
}));

export const StyledFilterStateItem = styled.li({
    ...filterRow,
    color: colors.text.secondary,
});

/**
 * The option's Checkbox is a visual indicator only — the item's own onClick drives selection.
 */
export const StyledOptionCheckbox = styled.span({
    pointerEvents: "none",
    display: "inline-flex",
});
